import { createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, Outlet, redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { isFailure } from './lib/api';
import { meQuery } from './lib/queries';
import { NotFoundPage, ServerErrorPage } from './components/ErrorPage';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet });

async function requireMe(qc: QueryClient) {
  try {
    return await qc.ensureQueryData(meQuery);
  } catch (e) {
    // a magic link that expired lands on /app/…?error=…: keep the reason for the login page (A12)
    if (isFailure(e, 'UNAUTHENTICATED')) throw redirect({ to: '/login', search: new URLSearchParams(window.location.search).has('error') ? { error: 'link' } : {} });
    throw e;
  }
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/app/today' });
  },
});

const resetRoute = createRoute({ getParentRoute: () => rootRoute, path: '/reset-password', component: lazyRouteComponent(() => import('./screens/Login'), 'ResetPassword') });
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // next: relative path only (open-redirect safe), e.g. back to the OAuth consent
  validateSearch: (s: Record<string, unknown>): { error?: string; next?: string } => ({
    error: typeof s.error === 'string' ? s.error : undefined,
    next: typeof s.next === 'string' && s.next.startsWith('/') && !s.next.startsWith('//') ? s.next : undefined,
  }),
  component: lazyRouteComponent(() => import('./screens/Login')),
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  beforeLoad: async ({ context }) => {
    await requireMe(context.queryClient);
  },
  component: lazyRouteComponent(() => import('./screens/Onboarding')),
});

const consentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/consent',
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch (e) {
      if (isFailure(e, 'UNAUTHENTICATED')) throw redirect({ to: '/login', search: { next: location.href } });
      throw e;
    }
  },
  component: lazyRouteComponent(() => import('./screens/Consent')),
});

const publicQuoteRoute = createRoute({ getParentRoute: () => rootRoute, path: '/q/$token', component: lazyRouteComponent(() => import('./screens/PublicQuote')) });
const publicShareRoute = createRoute({ getParentRoute: () => rootRoute, path: '/s/$token', component: lazyRouteComponent(() => import('./screens/PublicShare')) });
const printReportRoute = createRoute({ getParentRoute: () => rootRoute, path: '/print/report/$id', component: lazyRouteComponent(() => import('./screens/PrintReport')) });
const printQuoteRoute = createRoute({ getParentRoute: () => rootRoute, path: '/print/quote/$id', component: lazyRouteComponent(() => import('./screens/PrintQuote')) });

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  beforeLoad: async ({ context }) => {
    const me = await requireMe(context.queryClient);
    if (!me.account.onboarding.completed_at) throw redirect({ to: '/onboarding' });
  },
  component: lazyRouteComponent(() => import('./screens/AppLayout')),
});

const appIndex = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/app/today' });
  },
});
const taskDetailRoute = createRoute({ getParentRoute: () => appRoute, path: 'tasks/$id', component: lazyRouteComponent(() => import('./screens/TaskDetail')) });
const catalogRoute = createRoute({ getParentRoute: () => appRoute, path: 'catalog', component: lazyRouteComponent(() => import('./screens/Catalog')) });
const todayRoute = createRoute({ getParentRoute: () => appRoute, path: 'today', component: lazyRouteComponent(() => import('./screens/Today')) });
const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'tasks',
  validateSearch: (s: Record<string, unknown>): { engagement?: string; oos?: true; new?: true; view?: 'list' | 'week'; due?: 'overdue' | 'today' | 'week' | 'none'; week?: string } => ({
    engagement: typeof s.engagement === 'string' ? s.engagement : undefined,
    oos: s.oos === true || s.oos === 'true' ? true : undefined,
    new: s.new === true || s.new === 'true' ? true : undefined,
    view: s.view === 'list' || s.view === 'week' ? s.view : undefined,
    due: s.due === 'overdue' || s.due === 'today' || s.due === 'week' || s.due === 'none' ? s.due : undefined,
    week: typeof s.week === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.week) ? s.week : undefined,
  }),
  component: lazyRouteComponent(() => import('./screens/Tasks')),
});
const engagementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'engagements',
  validateSearch: (s: Record<string, unknown>): { new?: string } => ({ new: typeof s.new === 'string' ? s.new : undefined }),
  component: lazyRouteComponent(() => import('./screens/Engagements')),
});
const engagementRoute = createRoute({ getParentRoute: () => appRoute, path: 'engagements/$id', component: lazyRouteComponent(() => import('./screens/Engagement')) });
const pipelineRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'pipeline',
  validateSearch: (s: Record<string, unknown>): { view?: 'kanban' | 'list' | 'closed'; period?: 'quarter' | 'year' | 'all'; new?: string } => ({
    view: s.view === 'list' || s.view === 'closed' ? s.view : ('kanban' as const),
    period: s.period === 'year' || s.period === 'all' ? s.period : undefined,
    new: typeof s.new === 'string' ? s.new : undefined,
  }),
  component: lazyRouteComponent(() => import('./screens/Pipeline')),
});
const dealRoute = createRoute({ getParentRoute: () => appRoute, path: 'deals/$id', component: lazyRouteComponent(() => import('./screens/Deal')) });
const orgsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'organizations',
  validateSearch: (s: Record<string, unknown>): { tab?: 'active_client' | 'prospect' | 'former_client' | 'lost' | 'all'; q?: string; new?: true } => ({
    tab: ['active_client', 'prospect', 'former_client', 'lost', 'all'].includes(String(s.tab)) ? (String(s.tab) as 'active_client' | 'prospect' | 'former_client' | 'lost' | 'all') : undefined,
    q: typeof s.q === 'string' ? s.q : undefined,
    new: s.new === true || s.new === 'true' ? true : undefined,
  }),
  component: lazyRouteComponent(() => import('./screens/Organizations')),
});
const orgRoute = createRoute({ getParentRoute: () => appRoute, path: 'organizations/$id', component: lazyRouteComponent(() => import('./screens/Organization')) });
const searchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'search',
  validateSearch: (s: Record<string, unknown>): { q?: string; all?: true } => ({ q: typeof s.q === 'string' ? s.q : '', all: s.all === true || s.all === 'true' ? true : undefined }),
  component: lazyRouteComponent(() => import('./screens/Search')),
});
const settingsAccountRoute = createRoute({ getParentRoute: () => appRoute, path: 'settings/account', component: lazyRouteComponent(() => import('./screens/SettingsAccount')) });
const settingsPipelineRoute = createRoute({ getParentRoute: () => appRoute, path: 'settings/pipeline', component: lazyRouteComponent(() => import('./screens/SettingsPipeline')) });
const settingsSecurityRoute = createRoute({ getParentRoute: () => appRoute, path: 'settings/security', component: lazyRouteComponent(() => import('./screens/SettingsSecurity')) });
const settingsAiRoute = createRoute({ getParentRoute: () => appRoute, path: 'settings/ai', component: lazyRouteComponent(() => import('./screens/SettingsAi')) });
const settingsIndex = createRoute({
  getParentRoute: () => appRoute,
  path: 'settings',
  beforeLoad: () => {
    throw redirect({ to: '/app/settings/account' });
  },
});
const financeRoute = createRoute({ getParentRoute: () => appRoute, path: 'finance', component: lazyRouteComponent(() => import('./screens/Finance')) });
const proposalsRoute = createRoute({ getParentRoute: () => appRoute, path: 'proposals', component: lazyRouteComponent(() => import('./screens/Proposals')) });
const quoteRoute = createRoute({ getParentRoute: () => appRoute, path: 'quotes/$id', component: lazyRouteComponent(() => import('./screens/Quote')) });
const hoursRoute = createRoute({ getParentRoute: () => appRoute, path: 'hours', component: lazyRouteComponent(() => import('./screens/Hours')) });
const hoursDayRoute = createRoute({ getParentRoute: () => appRoute, path: 'hours/$date', component: lazyRouteComponent(() => import('./screens/Hours')) });
const delegationsRoute = createRoute({ getParentRoute: () => appRoute, path: 'delegations', component: lazyRouteComponent(() => import('./screens/Delegations')) });
const instructionsRoute = createRoute({ getParentRoute: () => appRoute, path: 'delegations/instructions', component: lazyRouteComponent(() => import('./screens/Delegations'), 'WorkInstructions') });
const notificationsRoute = createRoute({ getParentRoute: () => appRoute, path: 'notifications', component: lazyRouteComponent(() => import('./screens/Notifications')) });
const settingsNotificationsRoute = createRoute({ getParentRoute: () => appRoute, path: 'settings/notifications', component: lazyRouteComponent(() => import('./screens/SettingsNotifications')) });
const analysisRoute = createRoute({ getParentRoute: () => appRoute, path: 'analysis', component: lazyRouteComponent(() => import('./screens/Analysis')) });
const goalsRoute = createRoute({ getParentRoute: () => appRoute, path: 'goals', component: lazyRouteComponent(() => import('./screens/Goals')) });
const businessRoute = createRoute({ getParentRoute: () => appRoute, path: 'business', component: lazyRouteComponent(() => import('./screens/Business')) });
const profileRoute = createRoute({ getParentRoute: () => appRoute, path: 'profile', component: lazyRouteComponent(() => import('./screens/Profile')) });
const settingsPlanRoute = createRoute({ getParentRoute: () => appRoute, path: 'settings/plan', component: lazyRouteComponent(() => import('./screens/SettingsPlan')) });
const settingsDataRoute = createRoute({ getParentRoute: () => appRoute, path: 'settings/data', component: lazyRouteComponent(() => import('./screens/SettingsData')) });
const reviewRoute = createRoute({ getParentRoute: () => appRoute, path: 'review/$id', component: lazyRouteComponent(() => import('./screens/Review')) });
const moreRoute = createRoute({ getParentRoute: () => appRoute, path: 'more', component: lazyRouteComponent(() => import('./screens/More')) });
const soonRoute = createRoute({ getParentRoute: () => appRoute, path: 'soon/$area', component: lazyRouteComponent(() => import('./screens/Soon')) });

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  resetRoute,
  onboardingRoute,
  consentRoute,
  publicQuoteRoute,
  printQuoteRoute,
  publicShareRoute,
  printReportRoute,
  appRoute.addChildren([
    appIndex,
    todayRoute,
    tasksRoute,
    taskDetailRoute,
    catalogRoute,
    engagementsRoute,
    engagementRoute,
    pipelineRoute,
    dealRoute,
    orgsRoute,
    orgRoute,
    searchRoute,
    settingsIndex,
    settingsAccountRoute,
    settingsPipelineRoute,
    settingsAiRoute,
    settingsSecurityRoute,
    moreRoute,
    financeRoute,
    proposalsRoute,
    quoteRoute,
    hoursRoute,
    hoursDayRoute,
    delegationsRoute,
    instructionsRoute,
    reviewRoute,
    analysisRoute,
    goalsRoute,
    profileRoute,
    businessRoute,
    notificationsRoute,
    settingsNotificationsRoute,
    settingsPlanRoute,
    settingsDataRoute,
    soonRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: 'intent', defaultPreloadStaleTime: 0, scrollRestoration: true, defaultNotFoundComponent: NotFoundPage, defaultErrorComponent: ({ error }) => <ServerErrorPage error={error} /> });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
